// Shared submit-handler for create + edit station pages. Sends the payload
// to the new NestJS API: POST /stations for create, PATCH /stations/:slug
// for edit. Server-side validation errors surface as ApiError with the
// validation message; we let those bubble up so the form can render them.

import { ApiError, apiClient } from '../../../lib/apiClient';
import { invalidatePublisherCache } from '../../../data/publisherConfigs';
import type { PublisherFormPayload } from './PublisherForm';

type Mode = 'create' | 'update';

// `fn` arg is kept for backwards-compat with the existing call sites that
// pass the legacy edge function name; we map it onto the new operation.
const MODE_FOR_LEGACY_FN: Record<string, Mode> = {
  'selfservice-create-publisher': 'create',
  'selfservice-update-publisher': 'update',
};

export function usePublisherFormSubmit(
  fn: 'selfservice-create-publisher' | 'selfservice-update-publisher',
) {
  const mode = MODE_FOR_LEGACY_FN[fn] ?? 'create';
  return async (payload: PublisherFormPayload): Promise<{ slug: string }> => {
    try {
      const data =
        mode === 'create'
          ? await apiClient.post<{ slug: string }>('/stations', payload)
          : await apiClient.patch<{ slug: string }>(
              `/stations/${encodeURIComponent(payload.slug)}`,
              payload,
            );
      // Drop the station cache so the newly-written row is visible
      // immediately on subsequent navigation.
      invalidatePublisherCache();
      return data;
    } catch (e) {
      if (e instanceof ApiError) {
        // Defense-in-depth: the OnboardChannelPage gate should already prevent
        // unprovisioned users from reaching submit, but if they do, surface a
        // clearer message than the raw backend exception.
        if (e.status === 400 && /organization_id|organization context/i.test(e.message)) {
          throw new Error(
            "Your account isn't linked to an organization yet. Contact support to be assigned.",
          );
        }
        throw new Error(e.message || 'Save failed');
      }
      throw e;
    }
  };
}
